import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { emit, type Props } from '../../platform/analytics/analytics'
import type { ENUM_VALUES } from '../../platform/analytics/vocabulary'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import type { components } from '../../api/generated/schema'
import {
  accountsAddedCountOf,
  classifyLinkAbandonReason,
  LinkAbortError,
  type LinkAbandonReason,
} from './linkAbandonReason'
import type { PendingPlaidOAuth } from './providers/plaidOAuth'
import { relinkErrorOf, type RelinkError } from './relinkErrors'
import { isEffectivelyDisconnected } from './accountConnection'
import { listAccounts } from './useAccounts'
import { getAdapter, redirectFlowAdapter } from './providers/registry'
import type { CompletionPayload, LinkSession, ProviderType, ReconnectEntryPoint } from './providers/types'

/**
 * Every wire read/write for the link and reauth funnels — Ticket 3 (#397).
 *
 * `AccountsError`'s own shape, restated rather than shared: the two error
 * classes read different endpoints' bodies, and the 409 they each carry is a
 * different fact — a manual account's live-transaction count there, a
 * reauth/nonce outcome here. `AccountsError`'s own docblock is explicit that
 * `openapi.json` under-documents its own 409s (only 204/422 declared for the
 * delete endpoint it wraps); the same is true of every 409 in this file, so
 * bodies are read defensively rather than typed against the generated client.
 */
export class AggregatorError extends Error {
  readonly body: unknown
  readonly status: number

  constructor(body: unknown, status: number) {
    super(`Aggregator request failed with ${status}`)
    this.name = 'AggregatorError'
    this.body = body
    this.status = status
  }
}

/** `true` iff `body` names `reauth_required` — FastAPI's own error shape is
 *  `{ detail: ... }`, and `detail` may be a bare string or a structured
 *  object depending on how the exception was raised, so this reads
 *  defensively rather than assuming one. Returns `false`, never throws, for
 *  a body that is not the shape expected — same discipline as `useAccounts
 *  .ts`'s `transactionCountOf`: a caller reads a generic message instead of
 *  crashing on an unexpected 409 body. */
function namesReauthRequired(body: unknown): boolean {
  if (typeof body !== 'object' || body === null) return false
  const detail = (body as Record<string, unknown>).detail
  return typeof detail === 'string'
    ? detail.includes('reauth_required')
    : JSON.stringify(detail ?? '').includes('reauth_required')
}

/** One entry in the provider picker — `ProviderDescriptor`, camelCased. */
export type ProviderOption = {
  provider: ProviderType
  displayName: string
  subtitle: string | null
  isDefault: boolean
}

function toProviderOption(p: components['schemas']['ProviderDescriptor']): ProviderOption {
  return {
    provider: p.provider,
    displayName: p.display_name,
    subtitle: p.subtitle ?? null,
    isDefault: p.is_default ?? false,
  }
}

/**
 * The catalogue plus the REGION the server actually resolved — not just the
 * providers array. `ProviderCatalogResponse.region` echoes back whichever
 * region answered the request: the caller's explicit choice, or the
 * server's own resolution when none was sent. `Connect.tsx`'s region
 * selector reads this back as its displayed default (team-lead's own
 * condition, #397 follow-up) rather than showing a client-side guess ahead
 * of what the server actually did.
 */
export type ProviderCatalog = { region: string; providers: ProviderOption[] }

/**
 * `region: null` omits the query parameter entirely, rather than sending
 * `region=null` or a client-side guess — the backend's `region` is now
 * OPTIONAL (`openapi.json`, refreshed 2026-09-12) and resolves the same way
 * everything else does when absent (linked-account currency, then device
 * locale, then a default), which is exactly the answer a user with no
 * linked accounts — and therefore no `users.country_code` — needs. Sending
 * a device-locale guess as though it were the user's own choice would
 * silently win over that resolution, which is worse than not asking.
 */
async function listProviders(region: string | null): Promise<ProviderCatalog> {
  const { data, error, response } = await api.GET('/api/v1/aggregator/providers', {
    params: { query: region !== null ? { region } : {} },
  })
  if (error !== undefined || data === undefined) throw new AggregatorError(error, response.status)
  return { region: data.region, providers: data.providers.map(toProviderOption) }
}

/**
 * The provider catalogue — rendered, never hardcoded (#397's own checklist).
 * `live`, not `synced`: a catalogue is the backend's own provider-registry
 * state, not bank-sync freshness, so there is no `last_synced_at` to be
 * behind (see `surfaces.ts`'s `aggregator-providers` row).
 *
 * `region: null` means "let the server decide" — `Connect.tsx` passes this
 * until the user makes an explicit pick; see `listProviders`'s own docblock
 * for why sending a guess would be worse than sending nothing.
 *
 * **Empty is reachable and handled, not a crash** — a region with no
 * supported provider is a real, if rare, response shape, and gets the same
 * treatment `Connect.tsx` already owes a catalogue entry with no registered
 * adapter: told to the user, routed to manual entry, never thrown.
 */
export function useProviders(region: string | null): AsyncState<ProviderCatalog> {
  const query = useQuery({
    queryKey: keys.aggregator.providers(region),
    queryFn: () => listProviders(region),
  })
  return fromQuery(query, {
    message: () => 'Connection methods could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    isEmpty: (catalog) => catalog.providers.length === 0,
  })
}

/** One connection — `ProviderItemResponse`, camelCased. */
export type RelinkStatus = 'pending' | 'completed' | 'needs_review' | 'institution_mismatch'

function relinkStatusOf(raw: string | null | undefined): RelinkStatus | null {
  switch (raw) {
    case 'pending':
    case 'completed':
    case 'needs_review':
    case 'institution_mismatch':
      return raw
    default:
      return null
  }
}

export type ProviderItem = {
  id: string
  provider: ProviderType
  status: string
  institutionId: string | null
  institutionName: string | null
  institutionLogoUrl: string | null
  lastSyncedAt: Date | null
  createdAt: Date
  /** On the NEW item of a reconnect; `null` for an ordinary connection. */
  relinkStatus: RelinkStatus | null
  relinkedFromItemId: string | null
}

export function toProviderItem(i: components['schemas']['ProviderItemResponse']): ProviderItem {
  return {
    id: i.id,
    provider: i.provider,
    status: i.status,
    institutionId: i.institution_id ?? null,
    institutionName: i.institution_name ?? null,
    institutionLogoUrl: i.institution_logo_url ?? null,
    lastSyncedAt: i.last_synced_at != null ? new Date(i.last_synced_at) : null,
    createdAt: new Date(i.created_at),
    relinkStatus: relinkStatusOf(i.relink_status),
    relinkedFromItemId: i.relinked_from_item_id ?? null,
  }
}

async function listProviderItems(): Promise<ProviderItem[]> {
  const { data, error, response } = await api.GET('/api/v1/aggregator/items', { params: {} })
  if (error !== undefined || data === undefined) throw new AggregatorError(error, response.status)
  return data.map(toProviderItem)
}

/**
 * Every bank connection — `Connections.tsx`'s own surface, `synced`
 * (`surfaces.ts`'s `aggregator-items` row). `asOf` mirrors `useAccounts.ts`'s
 * `oldestStaleSync`, applied to items rather than accounts — the two lists
 * carry the identical field for the identical reason and are not shared as
 * one function because they read two different response shapes.
 */
export function useProviderItems(): AsyncState<ProviderItem[]> {
  const client = useQueryClient()
  const query = useQuery({
    queryKey: keys.aggregator.items(),
    queryFn: listProviderItems,
    // A reconnect still `pending` is being paired by the server on its sync
    // pages: watch it until it settles.
    refetchInterval: (q) => (q.state.data?.some((i) => i.relinkStatus === 'pending') ? 5000 : false),
  })
  // An item LEAVING `pending` has re-attached the old accounts to the new item
  // id, so the accounts list is stale: invalidate it once per such item.
  const pendingIds = useRef<Set<string>>(new Set())
  useEffect(() => {
    const items = query.data
    if (items === undefined) return
    const settled = items.some((i) => i.relinkStatus !== 'pending' && pendingIds.current.has(i.id))
    pendingIds.current = new Set(items.filter((i) => i.relinkStatus === 'pending').map((i) => i.id))
    if (settled) void client.invalidateQueries({ queryKey: keys.accounts.all() })
  }, [query.data, client])
  // Same key and fn as `useAccounts()`, so TanStack dedupes it. Read for its
  // `connection_status` only; its own failure is that surface's to report.
  const accountsQuery = useQuery({ queryKey: keys.accounts.list(), queryFn: listAccounts })
  return fromQuery(query, {
    message: () => 'Connections could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    isEmpty: (items) => items.length === 0,
    asOf: (items) => {
      // A disconnected account's item (provider-removed, still listed) has a
      // `last_synced_at` frozen at the disconnect; it must not keep this
      // surface stale. Which items those are comes from `connection_status` on
      // the accounts, never from `item.status`. While the accounts are still
      // PENDING nothing can be said yet, so no stale state is shown: judging
      // now would flash a stale banner for the one fetch it takes to learn the
      // old item is a disconnected one. If that query FAILS, nothing is
      // excluded and freshness reads off the items alone.
      if (accountsQuery.isPending) return undefined
      const disconnectedItemIds = new Set(
        (accountsQuery.data ?? []).flatMap((a) =>
          isEffectivelyDisconnected(a) && a.itemId !== null ? [a.itemId] : [],
        ),
      )
      const synced = items
        .filter((i) => !disconnectedItemIds.has(i.id))
        .map((i) => i.lastSyncedAt)
        .filter((d): d is Date => d !== null)
      if (synced.length === 0) return undefined
      const oldest = synced.reduce((min, d) => (d < min ? d : min))
      return Date.now() - oldest.getTime() > 24 * 60 * 60 * 1000 ? oldest : undefined
    },
  })
}

function toLinkSession(r: components['schemas']['LinkSessionResponse']): LinkSession {
  return {
    provider: r.provider,
    linkToken: r.link_token,
    callbackHost: r.callback_host ?? null,
    callbackPaths: r.callback_paths ?? null,
    stateNonce: r.state_nonce ?? null,
  }
}

/**
 * `region: null` omits the field from the request body rather than sending
 * it as `null` — same convention `useAccounts.ts`'s `useDeleteAccount`
 * already applies to `confirm_delete_transactions`, and the same reasoning
 * `listProviders` states above applies here identically: `LinkSessionRequest
 * .region` is optional, and an unset choice should read as "let the server
 * resolve it," not as a value.
 */
/**
 * `redirectUri` comes in already resolved, from
 * `adapter.redirectFlow?.redirectUri()` — NOT computed here off a
 * `provider === 'plaid'` comparison. `registry.test.ts`'s "no provider name
 * string literal appears outside registry.ts" (#397) is a real
 * architectural guard, and a per-adapter capability (the same shape
 * `reauthRoundTripsToken` already uses) is what keeps this file clean of it
 * while still letting only Plaid's adapter opt in.
 */
async function createLinkSession(
  region: string | null,
  provider: ProviderType,
  redirectUri: string | null,
): Promise<LinkSession> {
  const { data, error, response } = await api.POST('/api/v1/aggregator/link-session', {
    body: {
      ...(region !== null ? { region } : {}),
      provider,
      ...(redirectUri !== null ? { redirect_uri: redirectUri } : {}),
    },
  })
  if (error !== undefined || data === undefined) throw new AggregatorError(error, response.status)
  return toLinkSession(data)
}

async function createReauthSession(itemId: string, redirectUri: string | null): Promise<LinkSession> {
  const { data, error, response } = await api.POST(
    '/api/v1/aggregator/items/{item_id}/reauth-session',
    {
      params: { path: { item_id: itemId } },
      ...(redirectUri !== null ? { body: { redirect_uri: redirectUri } } : {}),
    },
  )
  if (error !== undefined || data === undefined) throw new AggregatorError(error, response.status)
  return toLinkSession(data)
}

/**
 * `POST /link-session` for a RECONNECT: the body is `{relink_item_id,
 * redirect_uri?}` and nothing else — the server ignores `provider`/`region`
 * here (the session uses the old item's provider), so sending them would only
 * suggest a caller's pick that does not exist.
 */
async function createRelinkSession(relinkItemId: string, redirectUri: string | null): Promise<LinkSession> {
  const { data, error, response } = await api.POST('/api/v1/aggregator/link-session', {
    body: {
      relink_item_id: relinkItemId,
      ...(redirectUri !== null ? { redirect_uri: redirectUri } : {}),
    },
  })
  if (error !== undefined || data === undefined) throw new AggregatorError(error, response.status)
  return toLinkSession(data)
}

/**
 * `metadata.state_nonce` is added here, once, rather than at each call site —
 * epic #6: "send it to `/link-session/complete` in `metadata` as
 * `state_nonce`". `null` (Plaid, or a Flinks session the flag never opened)
 * adds nothing, matching `useDeleteAccount`'s "omitted entirely when falsy"
 * convention for an optional field a strict backend only checks on presence.
 *
 * **`state_nonce` is deleted off a COPY of `completion.metadata` before
 * this repo's own value is set, not just spread after it** (architect
 * review, #397). Spread ordering alone protected this only because a real
 * Flinks session always carries a nonce today; `FlinksCallback.tsx` forwards
 * every raw query parameter as metadata, so a callback URL that happened to
 * echo its own `state_nonce`-named parameter would sit in `completion
 * .metadata` too, and a FUTURE session where `stateNonce` is legitimately
 * `null` (ordering giving nothing to override) would let that untrusted
 * value pass straight through instead. Deleting it first makes "our value
 * wins, or nothing does" true regardless of what the metadata already held.
 */
async function completeLinkSession(
  provider: ProviderType,
  completion: CompletionPayload,
  stateNonce: string | null,
  relinkItemId: string | null = null,
): Promise<components['schemas']['LinkSessionCompleteResponse']> {
  // `relinkItemId`, like the nonce, only ever arrives from a caller that read it
  // from its own held state (a started reconnect, or `sessionStorage` on a
  // Plaid return) — never from a URL or from `completion.metadata`.
  const metadata = { ...completion.metadata }
  delete metadata.state_nonce
  if (stateNonce !== null) metadata.state_nonce = stateNonce

  const { data, error, response } = await api.POST('/api/v1/aggregator/link-session/complete', {
    body: {
      public_token: completion.publicToken,
      provider,
      metadata,
      ...(relinkItemId !== null ? { relink_item_id: relinkItemId } : {}),
    },
  })
  if (error !== undefined || data === undefined) throw new AggregatorError(error, response.status)
  return data
}

async function completeReauth(itemId: string): Promise<void> {
  const { error, response } = await api.POST('/api/v1/aggregator/items/{item_id}/reauth/complete', {
    params: { path: { item_id: itemId } },
  })
  if (error !== undefined) throw new AggregatorError(error, response.status)
}

/**
 * The three outcomes a caller has to render differently.
 *
 * `manual_fallback` covers every hard failure — no adapter, a rejected
 * `open()` (the user closed the window, a network error), a rejected
 * completion — because M5-R11 asks the SAME thing of all of them: offer
 * manual entry rather than dead-ending. `message` still carries the specific
 * reason (CLAUDE.md: a generic "something went wrong" is a defect wherever
 * something specific is possible), it just does not change what the caller
 * renders alongside it.
 */
export type LinkFlowState =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'success' }
  | { status: 'manual_fallback'; message: string }
  /**
   * `useReauthFlow()` ONLY — a 409 `reauth_required` from `/reauth/complete`.
   * Its own word, not `idle` (architect review, #397, N6): `idle` is also
   * this state machine's REST state before anything starts, and a caller
   * that reads it to mean "the reconnect did not take" has to cross-check a
   * second variable (`Connections.tsx`'s own `reauthingId`) to tell the two
   * apart. Behaviourally, reusing `idle` meant a user who finished a real
   * bank login and had it REJECTED got no feedback at all — the button
   * reverted, the health note was unchanged, and nothing said why. "It is
   * not an error" (the ticket's own words) never meant "say nothing".
   */
  | { status: 'not_repaired' }

const NO_ADAPTER_MESSAGE =
  "This connection method isn't available yet in this app — try again after updating, or add the account manually."

/**
 * MFA is not resumed automatically. `LinkSessionCompleteResponse.status ===
 * 'mfa_required'` means the provider needs another round with the
 * `request_id` it returns, relaunching the provider's SDK — and the settled
 * `LinkSession` type (this plan's own "do not redesign" interface) carries no
 * slot for that id. Rather than widen a settled type or silently treat
 * "needs more verification" as success, this reports it through the SAME
 * `manual_fallback` state everything else in this list does. Flagged in the
 * implementation report as a scope boundary, not an oversight.
 *
 * **The copy does NOT invite a retry.** Decided by product and matched by
 * iOS from the same source: retrying re-triggers the identical one-time
 * passcode and fails the identical way, so "try connecting again" sends the
 * user around a loop that cannot succeed — the exact shape of the
 * `/reauth/complete` trap this same file documents elsewhere, just in a
 * sentence instead of a state machine. The two claims this message must
 * carry are that the bank is not available RIGHT NOW, and that manual entry
 * is the real alternative — nothing about trying again.
 */
const MFA_MESSAGE = "This bank can't be connected right now. You can add the account manually."

/**
 * The fresh-link funnel — `Connect.tsx`'s own hook.
 *
 * Entitlement gating is deliberately NOT this hook's job. M5-R12 says the
 * check fires before the link token is requested, and the caller is what
 * decides whether to call `start()` at all — folding the check in here would
 * make `useEntitlement` a dependency of a hook that has no other reason to
 * know about billing, and would not change WHEN the check runs, only where
 * its `if` lives.
 */
export function useLinkFlow(): {
  state: LinkFlowState
  start: (region: string | null, provider: ProviderType) => Promise<void>
  reset: () => void
} {
  const [state, setState] = useState<LinkFlowState>({ status: 'idle' })
  const client = useQueryClient()

  // `useCallback`/`useMemo` below, not a fresh object every render: a caller
  // (`Connections.tsx`) that lists this hook's return value in an effect's
  // dependency array needs a STABLE identity across renders that did not
  // change `state`, or the effect fires on every render regardless of what
  // actually changed — the exact failure a complete-and-honest dependency
  // array is supposed to catch, moved here instead of papered over at the
  // call site with a suppressed lint rule.
  const start = useCallback(
    async (region: string | null, provider: ProviderType) => {
      // `emit` BEFORE `setState` (architect review, #399): `emit` throws in
      // DEV on a registration bug (`analytics.ts`'s own deliberate contract),
      // and firing it before any state mutation means a throw here leaves
      // `state` exactly where it started — visible as a rejected promise in
      // the console, never a UI stuck on "Connecting…" with `pending` set
      // and nothing left to clear it.
      //
      // `country`, not a `region` key — `region.ts`'s own `REGIONS` are ISO
      // country codes (`'CA'`/`'US'`), so this rides the already-registered,
      // already-ISO-exempt `country` key rather than a second capitalised-
      // value carve-out for the identical shape (vocabulary.ts's own note).
      //
      // `entry_point: 'connect_page'` is a LITERAL, and today it is the
      // only value this key can ever carry — `Connect.tsx` is `useLinkFlow`'s
      // one caller (architect review, #399, non-blocking). Kept as a real
      // property rather than dropped: `Accounts.tsx`'s toolbar button, its
      // empty-state CTA and `ConnectAnotherTile` all route through this SAME
      // screen today, but a future entry that calls `start()` from somewhere
      // else (M1's onboarding wizard, per the M5 spec's own dependency note)
      // would need this slot to already exist rather than adding it then —
      // a forward-looking enum of one member, stated as such rather than
      // left to look like unfinished work.
      emit('link_started', { provider, country: region, entry_point: 'connect_page' })
      setState({ status: 'pending' })
      // `duration_seconds` on `account_linked` below — the one timestamp this
      // funnel had no source for before #399. Read here, at the one place a
      // fresh attempt begins, rather than threaded through `LinkSession` (a
      // settled, Tickets-3-5 interface this ticket may not redesign).
      const startedAt = Date.now()

      const adapter = getAdapter(provider)
      if (adapter === undefined) {
        setState({ status: 'manual_fallback', message: NO_ADAPTER_MESSAGE })
        emit('link_abandoned', { provider, country: region, reason_code: 'unsupported_by_provider' })
        return
      }

      /**
       * The outcome, decided entirely INSIDE `try`/`catch` with no `emit`
       * call anywhere in it — architect review, #399, taking `emit
       * ('account_linked', …)` out of the `try` it used to sit in. A guard
       * rejection there, before `setState({ status: 'success' })`, meant a
       * SUCCESSFUL link fell into the very `catch` below and was reported as
       * `link_abandoned` and rendered as `manual_fallback` — analytics
       * changing a real, user-visible outcome. `setState` below now always
       * runs before the matching `emit`, for every branch, so an analytics
       * failure can change what gets MEASURED and never what gets SHOWN.
       */
      let outcome:
        | { kind: 'success'; accountsAddedCount: number | null }
        | { kind: 'mfa_required' }
        | { kind: 'abandoned'; reasonCode: LinkAbandonReason; message: string }

      try {
        const redirectUri = adapter.redirectFlow?.redirectUri() ?? null
        const session = await createLinkSession(region, provider, redirectUri)
        // #414 — persisted before `open()`, so whatever a provider's own
        // Link UI needs to resume is already saved by the time it can
        // decide, after the user picks an institution, to navigate the
        // browser fully away — by then it is too late to write anything.
        // `redirectFlow.prepare` itself no-ops when `redirectUri` is `null`
        // (SEC-1, security review): nothing sent to the backend means Plaid
        // has no URL to return to, so persisting here would be unreadable
        // by construction.
        adapter.redirectFlow?.prepare(session, { kind: 'link' }, redirectUri)
        const completion = await adapter.open(session)
        const result = await completeLinkSession(provider, completion, session.stateNonce)

        outcome =
          result.status === 'mfa_required'
            ? { kind: 'mfa_required' }
            : { kind: 'success', accountsAddedCount: accountsAddedCountOf(completion.metadata) }
      } catch (cause) {
        const message = cause instanceof Error ? cause.message : 'The bank could not be connected.'
        // `AggregatorError` — a request to OUR OWN backend failed — is a
        // structural fact decided HERE, not inside the classifier: `backend`
        // follows regardless of the carried status, without reading its
        // undocumented body (`namesReauthRequired` above already documents
        // why that body's shape is not to be trusted). Everything else asks
        // `classifyLinkAbandonReason`, which reads the reason a `LinkAdapter`
        // structurally attached — never a user-facing sentence.
        const reasonCode = cause instanceof AggregatorError ? 'backend' : classifyLinkAbandonReason(cause)
        outcome = { kind: 'abandoned', reasonCode, message }
      }

      if (outcome.kind === 'success') {
        setState({ status: 'success' })
        void client.invalidateQueries({ queryKey: keys.accounts.all() })
        void client.invalidateQueries({ queryKey: keys.aggregator.items() })
        emit('account_linked', {
          provider,
          accounts_added_count: outcome.accountsAddedCount,
          duration_seconds: Math.round((Date.now() - startedAt) / 1000),
        })
      } else if (outcome.kind === 'mfa_required') {
        setState({ status: 'manual_fallback', message: MFA_MESSAGE })
        emit('link_abandoned', { provider, country: region, reason_code: 'mfa_required' })
      } else {
        setState({ status: 'manual_fallback', message: outcome.message })
        emit('link_abandoned', { provider, country: region, reason_code: outcome.reasonCode })
      }
    },
    [client],
  )

  const reset = useCallback(() => setState({ status: 'idle' }), [])

  return useMemo(() => ({ state, start, reset }), [state, start, reset])
}

/**
 * The reauth funnel — `Connections.tsx`'s own hook, one instance per screen.
 *
 * **`reauthRoundTripsToken` is read off the adapter, never a provider-name
 * comparison** — the settled contract's whole point (epic #6, this plan's
 * Ticket 3 header).
 *
 * **`/reauth/complete` is called exactly when the provider's own flow
 * reported success**, and nothing short of that: after `adapter.open()`
 * resolves for a provider that does not round-trip a token (Plaid — Link's
 * update mode IS the whole reconnect), or after `/link-session/complete`
 * itself resolves without asking for more verification (Flinks). A rejected
 * `open()` or a failed completion means the reconnect did not happen, and
 * calling `/reauth/complete` anyway would ask the backend to report status on
 * a repair that was never attempted.
 */
export function useReauthFlow(): {
  state: LinkFlowState
  start: (itemId: string, provider: ProviderType) => Promise<void>
  reset: () => void
} {
  const [state, setState] = useState<LinkFlowState>({ status: 'idle' })
  const client = useQueryClient()

  // Same reason `useLinkFlow` memoizes its return — a stable identity for a
  // caller (`Connections.tsx`) that depends on it in an effect.
  const start = useCallback(
    async (itemId: string, provider: ProviderType) => {
      setState({ status: 'pending' })
      emit('reauth_started', { provider })

      const adapter = getAdapter(provider)
      if (adapter === undefined) {
        setState({ status: 'manual_fallback', message: NO_ADAPTER_MESSAGE })
        return
      }

      try {
        const redirectUri = adapter.redirectFlow?.redirectUri() ?? null
        const session = await createReauthSession(itemId, redirectUri)
        // #414 — same reasoning as `useLinkFlow.start` above, with the item
        // being reconnected in place of `null`.
        adapter.redirectFlow?.prepare(session, { kind: 'reauth', itemId }, redirectUri)
        const completion = await adapter.open(session)

        if (adapter.reauthRoundTripsToken) {
          const result = await completeLinkSession(provider, completion, session.stateNonce)
          if (result.status === 'mfa_required') {
            setState({ status: 'manual_fallback', message: MFA_MESSAGE })
            return
          }
        }

        await completeReauth(itemId)
        emit('reauth_completed', { provider })
        setState({ status: 'success' })
        void client.invalidateQueries({ queryKey: keys.accounts.all() })
        void client.invalidateQueries({ queryKey: keys.aggregator.items() })
      } catch (cause) {
        // 409 reauth_required from /reauth/complete: the reconnect did not
        // take. `not_repaired` — NOT the manual_fallback/error path, per
        // #397's own checklist: "it means the reconnect did not take", which
        // is a fact about the bank connection, not about this app failing.
        // NOT `idle` either (N6, architect review) — that state means "rest",
        // and is what this machine starts in, so a consumer could not tell
        // "nothing has happened yet" from "something happened and failed"
        // without a second variable. This name says which on its own.
        if (cause instanceof AggregatorError && cause.status === 409 && namesReauthRequired(cause.body)) {
          setState({ status: 'not_repaired' })
          return
        }
        const message = cause instanceof Error ? cause.message : 'The connection could not be repaired.'
        setState({ status: 'manual_fallback', message })
      }
    },
    [client],
  )

  const reset = useCallback(() => setState({ status: 'idle' }), [])

  return useMemo(() => ({ state, start, reset }), [state, start, reset])
}

/** What a reconnect that did not finish says to the user. Fixed local copy,
 *  never the server's `message` (its prose is not a contract). */
function relinkMessageOf(error: RelinkFailure): string {
  switch (error.kind) {
    case 'not_found':
      return "This connection can't be reconnected any more. Refresh and try again."
    case 'use_reauth':
      return 'This bank is connected again. Repair it from Connections instead.'
    case 'already_relinked':
      return 'This bank was already reconnected.'
    case 'session_mismatch':
    case 'nonce_invalid':
    case 'nonce_unavailable':
      return 'The reconnect session could not be verified. Start again from Connections.'
    case 'provider_mismatch':
      return "This bank has to be reconnected with the connection method it was set up with."
    case 'already_linked':
      return 'That login is already one of your connections.'
    case 'mfa_required':
      return MFA_MESSAGE
    case 'no_adapter':
      return NO_ADAPTER_MESSAGE
    case 'abandoned':
      return 'The reconnect was not finished.'
    case 'link_failed':
    case 'unexpected':
      return "Something went wrong while reconnecting your bank. Check your connection and try again."
    default:
      return 'The bank could not be reconnected.'
  }
}

export type RelinkFailure =
  | RelinkError
  | { kind: 'nonce_unavailable' }
  | { kind: 'mfa_required' }
  | { kind: 'no_adapter' }
  /** The USER closed the bank window — the only `abandoned`. */
  | { kind: 'abandoned'; reasonCode: LinkAbandonReason }
  /** The bank window ended in a FAILURE (the adapter's own non-user reason code). */
  | { kind: 'link_failed'; reasonCode: LinkAbandonReason }
  /** Anything else thrown: a dropped connection (a fetch `TypeError`), a
   *  programming error. Never the user's doing, so never silent. */
  | { kind: 'unexpected' }

export type RelinkFlowState =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'started'; newItemId: string; relinkStatus: RelinkStatus; institutionName: string | null }
  | { status: 'failed'; error: RelinkFailure; message: string }

/** What a reconnect came to, for analytics: a started one (with its status) or a failure kind. */
export type ReconnectOutcome =
  | { status: 'started'; relinkStatus: RelinkStatus }
  | { status: 'failed'; kind: (typeof ENUM_VALUES.relink_error_kind)[number] }

/**
 * The ONE builder of the reconnect outcome event — the in-page flow and the OAuth
 * return both call it, so the two cannot drift apart. Enums, a duration in
 * seconds and the provider id; never a name, id or amount (rule 7). The duration runs
 * from `start()`, which both paths record, so they measure the same span.
 */
export function reconnectOutcomeEvent(input: {
  provider: string | null
  entryPoint: ReconnectEntryPoint | undefined
  startedAt: number | undefined
  outcome: ReconnectOutcome
}): { name: 'reconnect_completed' | 'reconnect_failed'; props: Props } {
  const { provider, entryPoint, startedAt, outcome } = input
  const common: Props = { provider, ...(entryPoint === undefined ? {} : { entry_point: entryPoint }) }
  if (outcome.status === 'started') {
    return {
      name: 'reconnect_completed',
      props: {
        ...common,
        relink_status: outcome.relinkStatus,
        ...(startedAt === undefined ? {} : { duration_seconds: Math.round((Date.now() - startedAt) / 1000) }),
      },
    }
  }
  return { name: 'reconnect_failed', props: { ...common, relink_error_kind: outcome.kind } }
}

function failed(error: RelinkFailure, message?: string): RelinkFlowState {
  return { status: 'failed', error, message: message ?? relinkMessageOf(error) }
}

/**
 * Reads a relink completion. A relink completion with no `relink` block, no
 * new item id, or one that names a DIFFERENT old item than the one this client
 * asked to reconnect is a contract breach: reported, never navigated to.
 * `relink.status` is an optional string on the wire; ABSENT reads as `pending`,
 * the only status a completion can legitimately omit, and an UNRECOGNISED one
 * is a contract breach like the rest.
 */
function relinkStartedOf(
  result: components['schemas']['LinkSessionCompleteResponse'],
  relinkItemId: string,
): Extract<RelinkFlowState, { status: 'started' }> | null {
  const relink = result.relink
  const newItemId = result.provider_item_id
  if (relink == null || !newItemId || relink.from_item_id !== relinkItemId) return null
  // ABSENT (`null`/`undefined`) is `pending`; a string this client does not
  // recognise is NOT — it is a status the screens have no copy for, so it is a
  // non-success (`null` here), never quietly read as the benign one.
  const relinkStatus = relink.status == null ? 'pending' : relinkStatusOf(relink.status)
  if (relinkStatus === null) return null
  return {
    status: 'started',
    newItemId,
    relinkStatus,
    institutionName: result.institution_name ?? null,
  }
}

/** A thrown failure from a reconnect call or its adapter, as flow state.
 *  `abandoned` is ONLY the user closing the bank window (`LinkAbortError`
 *  `user_cancelled`); every other throw is a failure with fixed copy, never the
 *  thrown error's own text (a dropped `/complete` after the bank sign-in must
 *  not read as the user cancelling, nor leak "Failed to fetch"). */
function relinkFailureOf(cause: unknown): RelinkFlowState {
  if (cause instanceof AggregatorError) return failed(relinkErrorOf(cause.status, cause.body))
  if (cause instanceof LinkAbortError) {
    return failed(
      cause.reason === 'user_cancelled'
        ? { kind: 'abandoned', reasonCode: cause.reason }
        : { kind: 'link_failed', reasonCode: cause.reason },
    )
  }
  return failed({ kind: 'unexpected' })
}

/**
 * The reconnect funnel — shares `useLinkFlow`/`useReauthFlow`'s helpers
 * (`getAdapter`, `completeLinkSession`, `redirectFlow.prepare`) and forks none.
 *
 * `relinkItemId` is the DISCONNECTED ACCOUNT's `itemId` from `GET /accounts`.
 *
 * **`redirect_uri` is sent before the provider is known.** The session's
 * provider (the old item's) is only on the RESPONSE, so the request carries
 * the `redirect_uri` of the registered adapter that HAS a redirect flow
 * (`redirectFlowAdapter`). The backend checks one allow-list for every
 * provider and a provider with no redirect flow (Flinks) ignores the field.
 * Everything after the session — the adapter, `open`, the completion — uses
 * `session.provider`, and `prepare` is only told a URI when that adapter IS
 * the one whose URI was sent, so nothing is persisted for a redirect the
 * session's provider was never given.
 *
 * **Nonce before sign-in.** A session with no `state_nonce` fails
 * `nonce_unavailable` BEFORE `adapter.open()`: the backend refuses a relink
 * completion without one, so opening Link would spend the user's bank sign-in
 * on a certain failure.
 *
 * **Analytics** (counts, enums and a duration; never a bank or id): `reconnect_started`
 * once the session returns (the provider is only known then), then `reconnect_completed`
 * or `reconnect_failed` once the outcome is decided. Outcome emits come AFTER `setState`
 * and outside any `try` (the #399 ruling `useLinkFlow` follows): analytics may change what
 * is measured, never what is shown. `reconnect_started` is the one emit before the state
 * settles, and a throw there resets to idle and rethrows.
 */
export function useRelinkFlow(): {
  state: RelinkFlowState
  start: (
    relinkItemId: string,
    entryPoint: 'connections' | 'accounts_panel' | 'link_prompt',
  ) => Promise<void>
  reset: () => void
} {
  const [state, setState] = useState<RelinkFlowState>({ status: 'idle' })
  const client = useQueryClient()

  const start = useCallback(
    async (
      relinkItemId: string,
      entryPoint: 'connections' | 'accounts_panel' | 'link_prompt',
    ) => {
      setState({ status: 'pending' })

      const startedAt = Date.now()
      // The provider is on the SESSION; a failure before it returns carries none.
      let provider: string | null = null
      let outcome: RelinkFlowState
      let redirecting: ReturnType<typeof redirectFlowAdapter> = undefined
      let redirectUri: string | null = null
      let session: Awaited<ReturnType<typeof createRelinkSession>> | null = null
      let sessionFailure: RelinkFlowState | null = null
      // The session call has its OWN try so that `reconnect_started` can sit
      // between the two outside any `catch` (#399): a registration throw there
      // must not be read as a failed reconnect.
      try {
        redirecting = redirectFlowAdapter()
        redirectUri = redirecting?.redirectFlow?.redirectUri() ?? null
        session = await createRelinkSession(relinkItemId, redirectUri)
      } catch (cause) {
        sessionFailure = relinkFailureOf(cause)
      }

      if (session === null || sessionFailure !== null) {
        outcome = sessionFailure ?? failed({ kind: 'unexpected' })
      } else {
        provider = session.provider
        try {
          emit('reconnect_started', { provider, entry_point: entryPoint })
        } catch (thrown) {
          // Not swallowed: the dev-time registration error still surfaces. Back to
          // idle so the flow is restartable; the button's own "Reconnecting…" is
          // reset by useRelinkOutcome.start, which clears its pendingId on rejection.
          setState({ status: 'idle' })
          throw thrown
        }
        try {
          const adapter = getAdapter(session.provider)
          if (adapter === undefined) {
            outcome = failed({ kind: 'no_adapter' })
          } else if (session.stateNonce === null) {
            outcome = failed({ kind: 'nonce_unavailable' })
          } else {
            adapter.redirectFlow?.prepare(
              session,
              { kind: 'relink', relinkItemId, entryPoint, startedAt },
              adapter === redirecting ? redirectUri : null,
            )
            const completion = await adapter.open(session)
            const result = await completeLinkSession(session.provider, completion, session.stateNonce, relinkItemId)
            outcome =
              result.status === 'mfa_required'
                ? failed({ kind: 'mfa_required' })
                : (relinkStartedOf(result, relinkItemId) ?? failed({ kind: 'other', status: 200 }))
          }
        } catch (cause) {
          outcome = relinkFailureOf(cause)
        }
      }

      setState(outcome)
      if (outcome.status === 'started' || (outcome.status === 'failed' && outcome.error.kind === 'already_relinked')) {
        void client.invalidateQueries({ queryKey: keys.accounts.all() })
        void client.invalidateQueries({ queryKey: keys.aggregator.items() })
      }
      // After the state and the invalidations (#399): a throw here cannot change
      // what the user sees.
      if (outcome.status === 'started' || outcome.status === 'failed') {
        const event = reconnectOutcomeEvent({
          provider,
          entryPoint,
          startedAt,
          outcome:
            outcome.status === 'started'
              ? { status: 'started', relinkStatus: outcome.relinkStatus }
              : { status: 'failed', kind: outcome.error.kind },
        })
        emit(event.name, event.props)
      }
    },
    [client],
  )

  const reset = useCallback(() => setState({ status: 'idle' }), [])

  return useMemo(() => ({ state, start, reset }), [state, start, reset])
}

/** What a RECONNECT's return resolved to — the same facts `useRelinkFlow`'s
 *  `started` state carries, so one mapping (`relinkOutcome.ts`) serves both
 *  entrances. `relinkStatus` is `null` only when the reconnect already existed
 *  (a 409 `already_relinked`), whose status is unknown. */
export type RelinkedReturn = {
  newItemId: string
  relinkStatus: RelinkStatus | null
  institutionName: string | null
}

/**
 * `/plaid`'s own funnel (`PlaidOAuthReturn.tsx`) — #414. Picks a link or
 * reauth flow back up after Plaid's OAuth handoff destroyed everything
 * `useLinkFlow`/`useReauthFlow` were doing, using whatever
 * `providers/plaidOAuth.ts` persisted before the navigation.
 *
 * **Reuses `LinkFlowState`** rather than inventing a parallel status union —
 * the two exceptional outcomes this can reach (`mfa_required`, a 409
 * `reauth_required` on a reauth) are the SAME two `useLinkFlow`/
 * `useReauthFlow` already name, for the identical reasons documented on
 * those states above.
 *
 * **Reaches `resume` through `getAdapter(pending.provider)`, never a direct
 * import of `./providers/plaid`** (architect review, #414, B2 — an earlier
 * draft imported `resumePlaidOAuthLink` straight from that module, which
 * `registry.ts`'s own docblock rules out: "no call site outside this file
 * ever compares a provider name," and a static import of a concrete adapter
 * is that rule's bypass in a stronger form than the string literal
 * `registry.test.ts` greps for). Flinks has no `redirectFlow` at all — its
 * own OAuth-shaped flow never leaves the page — so a missing adapter or a
 * missing `redirectFlow` is treated identically to `useLinkFlow`/
 * `useReauthFlow`'s own "no registered adapter" case.
 *
 * **`receivedRedirectUri` is `origin + pathname + search`, never
 * `window.location.href`** (security review, #414, SEC-2) — a fragment is
 * never part of what Plaid returns and has no reason to reach the SDK.
 *
 * `resume` takes the imperative shape (a plain function, not returning a
 * `Promise`) because its caller — `PlaidOAuthReturn.tsx` — fires it once
 * from an effect keyed on arrival, not from a click; the `state` it renders
 * off is the same discriminated union `Connect.tsx`/`Connections.tsx`
 * already know how to display.
 */
export function usePlaidOAuthResume(): {
  state: LinkFlowState
  /** Set once a RECONNECT's return completed (or found it already completed):
   *  where the screen should go (`relinkStatus` is `null` when the reconnect
   *  already existed and its status is unknown). `null` for a link or reauth. */
  relinked: RelinkedReturn | null
  resume: (pending: PendingPlaidOAuth) => void
} {
  const [state, setState] = useState<LinkFlowState>({ status: 'idle' })
  const [relinked, setRelinked] = useState<RelinkedReturn | null>(null)
  const client = useQueryClient()

  const resume = useCallback(
    (pending: PendingPlaidOAuth) => {
      setState({ status: 'pending' })
      // What a RECONNECT's return came to, reported once in `finally` below — after the
      // state is set and outside every `catch` (#399), so an analytics throw cannot
      // change what the screen shows. The stored entry was consumed before this ran, so a
      // reload or a StrictMode remount finds nothing to report again.
      let reported: ReconnectOutcome | null = null
      void (async () => {
        try {
          const adapter = getAdapter(pending.provider)
          if (adapter?.redirectFlow === undefined) {
            setState({ status: 'manual_fallback', message: NO_ADAPTER_MESSAGE })
            if (pending.kind === 'relink') reported = { status: 'failed', kind: 'no_adapter' }
            return
          }

          const receivedRedirectUri = `${window.location.origin}${window.location.pathname}${window.location.search}`
          const completion = await adapter.redirectFlow.resume(pending.linkToken, receivedRedirectUri)

          if (pending.kind === 'link') {
            // `pending.provider`/`pending.stateNonce` — data the persisted
            // session carried, never a literal or a hardcoded `null` — is
            // what keeps this file clean of the provider-name string
            // `registry.test.ts` polices (#397) and in step with the other
            // two entrances to this same completion call (B1, architect
            // review: `session.stateNonce` is not Plaid-only in the schema).
            const result = await completeLinkSession(pending.provider, completion, pending.stateNonce)
            if (result.status === 'mfa_required') {
              setState({ status: 'manual_fallback', message: MFA_MESSAGE })
              return
            }
          } else if (pending.kind === 'relink') {
            // The id and the nonce are the STORED ones (`isPendingPlaidOAuth`
            // already refused a relink missing either) — never read off the
            // return URL, which anyone can craft. The server then validates the
            // nonce (single use, this user, this `relink_item_id`).
            const result = await completeLinkSession(
              pending.provider,
              completion,
              pending.stateNonce,
              pending.relinkItemId,
            )
            if (result.status === 'mfa_required') {
              setState({ status: 'manual_fallback', message: MFA_MESSAGE })
              reported = { status: 'failed', kind: 'mfa_required' }
              return
            }
            const started = relinkStartedOf(result, pending.relinkItemId)
            if (started === null) {
              setState({ status: 'manual_fallback', message: relinkMessageOf({ kind: 'other', status: 200 }) })
              reported = { status: 'failed', kind: 'other' }
              return
            }
            reported = { status: 'started', relinkStatus: started.relinkStatus }
            setRelinked({
              newItemId: started.newItemId,
              relinkStatus: started.relinkStatus,
              institutionName: started.institutionName,
            })
          } else {
            // Plaid never round-trips a token on reauth (`reauthRoundTripsToken:
            // false`, `plaid.ts`) — Link's update mode IS the whole reconnect,
            // same as `useReauthFlow.start` above.
            await completeReauth(pending.itemId)
          }

          setState({ status: 'success' })
          void client.invalidateQueries({ queryKey: keys.accounts.all() })
          void client.invalidateQueries({ queryKey: keys.aggregator.items() })
        } catch (cause) {
          if (pending.kind === 'relink') {
            const failure = relinkFailureOf(cause)
            if (failure.status === 'failed' && failure.error.kind === 'already_relinked') {
              // Two tabs, or a double click: that reconnect already exists, so
              // the user goes to ITS match step rather than to an error.
              setRelinked({ newItemId: failure.error.itemId, relinkStatus: null, institutionName: null })
              setState({ status: 'success' })
              void client.invalidateQueries({ queryKey: keys.accounts.all() })
              void client.invalidateQueries({ queryKey: keys.aggregator.items() })
              reported = { status: 'failed', kind: 'already_relinked' }
            } else if (failure.status === 'failed') {
              setState({ status: 'manual_fallback', message: failure.message })
              reported = { status: 'failed', kind: failure.error.kind }
            }
            return
          }
          if (
            pending.kind === 'reauth' &&
            cause instanceof AggregatorError &&
            cause.status === 409 &&
            namesReauthRequired(cause.body)
          ) {
            setState({ status: 'not_repaired' })
            return
          }
          const message = cause instanceof Error ? cause.message : 'The connection could not be completed.'
          setState({ status: 'manual_fallback', message })
        } finally {
          if (pending.kind === 'relink' && reported !== null) {
            const event = reconnectOutcomeEvent({
              provider: pending.provider,
              entryPoint: pending.entryPoint,
              startedAt: pending.startedAt,
              outcome: reported,
            })
            emit(event.name, event.props)
          }
        }
      })()
    },
    [client],
  )

  return useMemo(() => ({ state, relinked, resume }), [state, relinked, resume])
}
